import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, DialogContent, DialogTitle, Box, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  ClassificationGroupView,
  CreateClasssificationGroupRequest,
  UpdateClassificationGroupRequest,
} from '../../../../generated/ApiClient';
import RestHttpClient from '../../../../common/RestHttpClient';
import { EnhancedDialog } from '../../../../component/EnhancedDialog';
import { FormTextField } from '../../../../component/form/FormTextField';
import { FormDropdown } from '../../../../component/form/FormDropdown';
import { useQuery } from 'react-query';
import { ClassificationContext } from '../../context/ClassificationContext.types';

interface FeatureDialogProps {
  isOpen: boolean;
  group: ClassificationGroupView | null;
  onClose: () => void;
}

const GroupDialog = ({ isOpen, group, onClose }: FeatureDialogProps) => {
  const { t } = useTranslation();

  const { refetchGroups } = useContext(ClassificationContext);

  const isEdit = !!group;

  const schema = z.object({
    name: z.string().min(1),
    parentGroupId: z.string().optional(),
  });

  type FormValues = z.infer<typeof schema>;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {},
  });

  useEffect(() => {
    reset(group ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, isOpen]);

  const handleClose = () => {
    onClose();
  };

  const { data: flatGroups = [] } = useQuery({
    queryKey: ['classification', 'groups'],
    queryFn: () => RestHttpClient.getClassificationGroups(),
    select: (x) => x.data,
  });

  const groupOptions = flatGroups.map((x) => ({ label: x.name, value: x.id }));

  const onFormSubmit = async (data: FormValues) => {
    try {
      if (isEdit && group) {
        await RestHttpClient.updateClassificationGroup(
          group.id,
          new UpdateClassificationGroupRequest({
            id: group.id,
            name: data.name,
            parentId: data.parentGroupId,
          })
        );
      } else {
        await RestHttpClient.createClassificationGroup(
          new CreateClasssificationGroupRequest({
            name: data.name,
            parentId: data.parentGroupId,
          })
        );
      }
      await refetchGroups();
      handleClose();
    } catch (error) {
      console.error('Error saving feature:', error);
    }
  };
  return (
    <EnhancedDialog isDialogOpened={isOpen} setIsDialogOpened={onClose}>
      <DialogTitle>
        {isEdit ? t('classification.groups.editGroup') : t('classification.groups.addGroup')}
      </DialogTitle>
      <DialogContent dividers>
        <form id="feature-form" onSubmit={handleSubmit(onFormSubmit)} noValidate>
          <Grid container spacing={3} sx={{ paddingY: 2 }}>
            <Grid item xs={12}>
              <FormTextField
                control={control}
                name="name"
                label={t('classification.groups.name')}
                disabled={isSubmitting}
                shrink
              />
            </Grid>
            <Grid item xs={12}>
              <FormDropdown
                control={control}
                name="parentGroupId"
                label={t('classification.groups.parent')}
                disabled={isSubmitting}
                options={groupOptions}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Box sx={{ textAlign: 'right' }}>
          <Button
            onClick={handleClose}
            sx={{ border: 1, borderColor: 'black', color: 'black', marginRight: '4px' }}
            disabled={isSubmitting}
          >
            {t('cancel')}
          </Button>
          <Button type="submit" form="feature-form" variant="contained" disabled={isSubmitting}>
            {t('save')}
          </Button>
        </Box>
      </DialogActions>
    </EnhancedDialog>
  );
};

export default GroupDialog;
